import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { OpenInNew, RestartAlt } from '@mui/icons-material';

const MIN = 1, MAX = 8;

/**
 * Visore immagine con zoom: rotellina (verso il puntatore), drag per pan,
 * pinch a due dita su mobile, doppio click per 2.5x/reset.
 */
export default function ZoomableImage({ src, alt, height = '58vh', counter }) {
  const boxRef = useRef(null);
  const [t, setT] = useState({ scale: 1, tx: 0, ty: 0 });
  const pointers = useRef(new Map());
  const pinchStart = useRef(null);
  const panStart = useRef(null);

  useEffect(() => { setT({ scale: 1, tx: 0, ty: 0 }); }, [src]);

  const clamp = useCallback((next) => {
    const el = boxRef.current;
    if (!el) return next;
    const scale = Math.min(MAX, Math.max(MIN, next.scale));
    const maxX = (scale - 1) * el.clientWidth / 2;
    const maxY = (scale - 1) * el.clientHeight / 2;
    return {
      scale,
      tx: Math.min(maxX, Math.max(-maxX, next.tx)),
      ty: Math.min(maxY, Math.max(-maxY, next.ty)),
    };
  }, []);

  const zoomAt = useCallback((cx, cy, factor) => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = cx - r.left - r.width / 2;   // punto in coordinate centro-box
    const py = cy - r.top - r.height / 2;
    setT(prev => {
      const scale = Math.min(MAX, Math.max(MIN, prev.scale * factor));
      const k = scale / prev.scale;
      const next = { scale, tx: px - k * (px - prev.tx), ty: py - k * (py - prev.ty) };
      return clamp(scale === 1 ? { scale: 1, tx: 0, ty: 0 } : next);
    });
  }, [clamp]);

  // wheel nativo (passive:false, altrimenti il preventDefault non ferma lo scroll del drawer)
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.25 : 1 / 1.25);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onPointerDown = (e) => {
    if (e.target.closest('button')) return;   // i pulsanti sovrapposti devono ricevere il click
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { d: Math.hypot(a.x - b.x, a.y - b.y), scale: t.scale };
      panStart.current = null;
    } else if (t.scale > 1) {
      panStart.current = { x: e.clientX, y: e.clientY, tx: t.tx, ty: t.ty };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      // copie locali: React può eseguire l'updater dopo che onPointerUp ha azzerato i ref
      const { d: d0, scale: s0 } = pinchStart.current;
      const box = boxRef.current;
      if (!box) return;
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
      const el = box.getBoundingClientRect();
      const px = cx - el.left - el.width / 2, py = cy - el.top - el.height / 2;
      setT(prev => {
        const scale = Math.min(MAX, Math.max(MIN, s0 * (d / d0)));
        const k = scale / prev.scale;
        return clamp({ scale, tx: px - k * (px - prev.tx), ty: py - k * (py - prev.ty) });
      });
    } else if (panStart.current) {
      const { x, y, tx, ty } = panStart.current;
      setT(prev => clamp({ scale: prev.scale, tx: tx + (e.clientX - x), ty: ty + (e.clientY - y) }));
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;
  };

  const zoomed = t.scale > 1.01;

  return (
    <Box ref={boxRef}
         onPointerDown={onPointerDown} onPointerMove={onPointerMove}
         onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
         onDoubleClick={(e) => zoomAt(e.clientX, e.clientY, zoomed ? 0 : 2.5)}
         sx={{ position: 'relative', height, flexShrink: 0, borderRadius: 2.5, overflow: 'hidden', bgcolor: '#0f172a',
               touchAction: 'none', userSelect: 'none',
               cursor: zoomed ? 'grab' : 'zoom-in', '&:active': { cursor: zoomed ? 'grabbing' : 'zoom-in' } }}>
      <img src={src} alt={alt} draggable={false}
           style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                    transform: `translate(${t.tx}px, ${t.ty}px) scale(${t.scale})`,
                    transition: panStart.current || pinchStart.current ? 'none' : 'transform 120ms ease-out' }} />
      {counter && (
        <Chip size="small" label={counter}
              sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'rgba(15,23,42,.75)', color: 'white' }} />
      )}
      <Chip size="small" label={zoomed ? `${t.scale.toFixed(1)}×` : 'rotella / doppio click per zoom'}
            sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: 'rgba(15,23,42,.6)', color: 'white', fontSize: 11 }} />
      <Box onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
           sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
        {zoomed && (
          <Tooltip title="Reimposta zoom">
            <IconButton size="small" onClick={() => setT({ scale: 1, tx: 0, ty: 0 })}
                        sx={{ bgcolor: 'rgba(15,23,42,.6)', color: 'white', '&:hover': { bgcolor: 'rgba(15,23,42,.85)' } }}>
              <RestartAlt fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Apri l'originale in una nuova scheda (con zoom)">
          <IconButton size="small"
                      onClick={(e) => { e.stopPropagation(); window.open(`/viewer.html?src=${encodeURIComponent(src)}&title=${encodeURIComponent(alt || 'Foto cartellone')}`, '_blank'); }}
                      sx={{ bgcolor: 'rgba(15,23,42,.6)', color: 'white', '&:hover': { bgcolor: 'rgba(15,23,42,.85)' } }}>
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
